/** Types for AMI measured analytics (Phase 4 Plan 2). Mirror of
 *  src/topology_platform/schemas/ami_analytics.py — keep in sync. */

import { LOADING_COLORS } from './engineering.ts'
import type { LoadingBasis } from './engineering.ts'

const LOADING_COLORS_BASE = LOADING_COLORS as Record<
  'overloaded' | 'heavy' | 'normal' | 'underloaded' | 'no_rating', string
>

/** One active meter's trailing-window measured rollup (meter_load_window).
 *  Populated per meter on the per-transformer path. Silent meters (no AMI
 *  energy rollup — a stale-GIS worklist) carry silent=true and null stats.
 *  Per-meter peaks are NOT coincident — they do not sum to the bank peak. */
export interface MeterWindowEntry {
  meter_id: number
  meter_number: string | null
  peak_kw: number | null
  peak_day: string | null
  avg_daily_kwh: number | null
  days_with_data: number | null
  coverage_pct: number | null
  /** Coincident contribution: this meter's kW AT the bank's measured peak hour,
   *  and that peak instant (ISO 8601, UTC). Unlike peak_kw, these DO sum to the
   *  bank peak across the transformer's meters. bank_peak_at is null when the
   *  transformer has no measured daily row in the window; kw_at_bank_peak is also
   *  null for a meter that did not report at that hour (a real 0.0 stays 0.0). */
  kw_at_bank_peak: number | null
  bank_peak_at: string | null
  silent: boolean
}

export interface MeasuredTransformerLoading {
  transformer_id: number
  name: string | null
  gis_id: string | null
  kva_rating: number | null
  feeder_id: number | null
  feeder_name: string | null
  meter_count: number
  meters_reporting: number
  days_with_data: number
  peak_kw_max: number | null
  max_loading_pct: number | null
  p95_loading_pct: number | null
  days_over_100: number
  window_coverage_pct: number
  estimated_kw: number
  estimated_loading_pct: number | null
  /** Hybrid measured+estimated overlay (spec §3.4). missing = max(meter_count
   *  - meters_reporting, 0); the effective peak adds a flat avg_demand_kw for
   *  each silent meter on top of the measured coincident peak (deliberately
   *  conservative). */
  meters_missing: number
  estimated_missing_kw: number
  /** peak_kw_max + missing × avg_demand_kw; populated for 'measured' AND
   *  'hybrid' basis, null for 'model' basis or when peak_kw_max is null. */
  effective_peak_kw: number | null
  effective_loading_pct: number | null
  /** 'measured' (coverage OK, no silent meters) | 'hybrid' (coverage OK, some
   *  silent meters) | 'model' (no reporting / below coverage). */
  loading_basis: LoadingBasis
  /** Silent meter numbers (no AMI rollup in the window) — populated only on the
   *  per-transformer endpoint; null on the summary/list endpoints. Doubles as a
   *  GIS-cleanup worklist. */
  silent_meter_numbers: string[] | null
  /** Per-meter measured window rollup — one entry per active meter on the
   *  transformer; populated ONLY on the per-transformer endpoint (null on the
   *  summary/list endpoints). */
  meters?: MeterWindowEntry[] | null
  loading_status: MeasuredLoadingStatus
  lat: number | null
  lon: number | null
}

export interface MeasuredLoadingSummary {
  window_days: number
  as_of: string | null
  total: number
  overloaded: number
  heavy: number
  normal: number
  underloaded: number
  no_rating: number
  insufficient_data: number
  /** Rows on the 'hybrid' basis (measured peak + estimated silent meters);
   *  these classify into the status buckets above, not insufficient_data. */
  hybrid: number
  transformers: MeasuredTransformerLoading[]
}

export interface TransformerLoadProfileDay {
  date: string
  energy_kwh: number
  peak_kw: number
  peak_at: string
  avg_kw: number
  loading_pct: number | null
  meters_reporting: number
  meter_count: number
  interval_coverage_pct: number
}

export interface TransformerLoadProfile {
  transformer_id: number
  name: string | null
  kva_rating: number | null
  days: TransformerLoadProfileDay[]
}

export interface VoltageViolationMeter {
  meter_id: number
  meter_number: string | null
  phase: string
  transformer_id: number | null
  transformer_name: string | null
  feeder_id: number | null
  nominal_v: number | null
  hours_below: number
  hours_above: number
  hours_reporting: number
  violation_rate_pct: number
  worst_v_min_pu: number | null
  worst_v_max_pu: number | null
  sag_total: number
  swell_total: number
  days_with_violations: number
  lat: number | null
  lon: number | null
}

export interface VoltageViolationsSummary {
  window_days: number
  as_of: string | null
  total_violation_meters: number
  meters: VoltageViolationMeter[]
}

export interface ModelComparisonTransformer {
  transformer_id: number
  name: string | null
  meter_count: number
  measured_avg_kw: number | null
  modeled_voltage_pu: number | null
  measured_avg_pu: number | null
  deviation_pu: number | null
  /** deviation_pu minus the feeder median (systematic offset removed);
   *  null when not comparable. */
  offset_corrected_deviation_pu: number | null
  load_coverage_pct: number | null
  voltage_coverage_pct: number | null
  flag: 'ok' | 'disagrees' | 'not_comparable'
  not_comparable_reason: string | null
}

export interface ModelComparisonResult {
  feeder_id: number
  feeder_name: string | null
  window_days: number
  power_factor_used: number
  load_basis: string
  deviation_threshold_pu: number
  caveat: string
  total: number
  ok: number
  disagrees: number
  not_comparable: number
  /** Median deviation_pu over comparable transformers (systematic-offset
   *  indicator); null when nothing is comparable. */
  median_deviation_pu: number | null
  transformers: ModelComparisonTransformer[]
}

export interface AmiStatus {
  data_available: boolean
  as_of: string | null
  /** Freshness threshold (hours) from server config, and the server-side
   *  verdict — all consumers key off ONE rule (spec §3.4). */
  staleness_hours_threshold: number
  is_stale: boolean
  /** Fleet-median measured coincident peak kW per meter (30-day window);
   *  calibrates the Avg Demand slider. Null when no AMI data. */
  fleet_median_coincident_kw_per_meter: number | null
  last_run_status: string | null
  last_run_started_at: string | null
  last_run_finished_at: string | null
  last_window_start: string | null
  last_window_end: string | null
  meters_matched: number
  meters_unmatched: number
  meters_nominal_unresolved: number
  last_error: string | null
}

/** Map colors for measured loading statuses (extends LOADING_COLORS with
 *  the coverage-gated status). */
export type MeasuredLoadingStatus =
  | 'overloaded'
  | 'heavy'
  | 'normal'
  | 'underloaded'
  | 'no_rating'
  | 'insufficient_data'

/** Same palette as LOADING_COLORS for the shared statuses (legends stay
 *  consistent when both layers are visible) + purple for the
 *  coverage-gated status that only measured data has. */
export const MEASURED_STATUS_COLORS: Record<MeasuredLoadingStatus, string> = {
  ...LOADING_COLORS_BASE,
  insufficient_data: '#a78bfa',
}
